import axios from 'axios'

const BASE = import.meta.env.VITE_HOLD_ALLOCATION_API_BASE || '/mock-api'

const client = axios.create({ baseURL: BASE, timeout: 120000 })

// Hard server-side limit for releaseComment / applyComment.
// Tecsys hard cap is 40 chars total; the service prepends a 4-char role prefix
// (RCD:/FIN:/OPS:/QA:) so the user-facing budget is 36.
export const HOLD_COMMENT_MAX = 36

/**
 * Normalises an axios error into the BaseResponse error envelope used by the
 * hold-allocation API. Returns a stable shape regardless of network failure,
 * HTTP error response, or thrown exception.
 *
 * @param {unknown} err
 * @returns {{ errorCode: string|null, message: string, status: number|null, data: any }}
 */
export const extractApiError = (err) => {
  if (err && err.response && err.response.data) {
    const body = err.response.data
    return {
      errorCode: body.errorCode || null,
      message: body.message || err.message || 'Request failed',
      status: typeof body.status === 'number' ? body.status : err.response.status || null,
      data: body.data || null,
    }
  }
  return {
    errorCode: null,
    message: (err && err.message) || 'Network error',
    status: null,
    data: null,
  }
}

export const getDashboard = (role = 'CUSTOMER_SERVICE', org) => {
  const headers = { 'X-Mock-Role': role }
  if (org) headers['X-Mock-Org'] = org
  return client.get('/dashboard', { headers })
}

export const getOrders = ({ role = 'CUSTOMER_SERVICE', bucket, search, holdCode, org } = {}) => {
  const params = {}
  if (bucket) params.bucket = bucket
  if (search) params.search = search
  if (holdCode) params.holdCode = holdCode
  const headers = { 'X-Mock-Role': role }
  if (org) headers['X-Mock-Org'] = org
  return client.get('/orders', { headers, params })
}

export const getFilterOptions = ({ role = 'CUSTOMER_SERVICE', bucket, org } = {}) => {
  const params = {}
  if (bucket) params.bucket = bucket
  const headers = { 'X-Mock-Role': role }
  if (org) headers['X-Mock-Org'] = org
  return client.get('/orders/filter-options', { headers, params })
}

export const getOrderDetail = (orderId, role = 'CUSTOMER_SERVICE', org) => {
  const headers = { 'X-Mock-Role': role }
  if (org) headers['X-Mock-Org'] = org
  return client.get(`/orders/${orderId}`, { headers })
}

export const getOrderHolds = (orderId, role = 'CUSTOMER_SERVICE') =>
  client.get(`/orders/${orderId}/holds`, { headers: { 'X-Mock-Role': role } })

export const releaseHold = (
  orderId,
  holdId,
  role = 'CUSTOMER_SERVICE',
  username = 'demo-user',
  body = {},
) =>
  client.post(`/orders/${orderId}/holds/${holdId}/release`, body, {
    headers: { 'X-Mock-Role': role, Username: username },
  })

export const releaseAllHolds = (
  orderId,
  role = 'CUSTOMER_SERVICE',
  username = 'demo-user',
  body = {},
) =>
  client.post(`/orders/${orderId}/holds/release-all`, body, {
    headers: { 'X-Mock-Role': role, Username: username },
    // Treat 207 Multi-Status as success so the thunk fulfilled handler can
    // inspect items[] and surface a partial-release warning.
    validateStatus: (s) => (s >= 200 && s < 300) || s === 207,
  })

export const reapplyHold = (
  orderId,
  holdId,
  role = 'CUSTOMER_SERVICE',
  username = 'demo-user',
  body = {},
) =>
  client.post(`/orders/${orderId}/holds/${holdId}/reapply`, body, {
    headers: { 'X-Mock-Role': role, Username: username },
  })

export const getAllocationLines = (orderId, org) => {
  const headers = {}
  if (org) headers['X-Mock-Org'] = org
  return client.get(`/orders/${orderId}/allocation-lines`, { headers })
}

export const prefillAllocation = (orderId, org) => {
  const headers = {}
  if (org) headers['X-Mock-Org'] = org
  return client.post(`/orders/${orderId}/allocation/prefill`, null, { headers })
}

export const submitAllocation = (
  orderId,
  role = 'CUSTOMER_SERVICE',
  username = 'demo-user',
  body,
  org,
) => {
  const headers = { 'X-Mock-Role': role, Username: username }
  if (org) headers['X-Mock-Org'] = org
  // Treat 207 Multi-Status as success so the thunk can inspect lineResults[]
  // and surface a partial-allocation warning.
  return client.post(`/orders/${orderId}/allocation/submit`, body, {
    headers,
    validateStatus: (s) => (s >= 200 && s < 300) || s === 207,
  })
}

/**
 * Verify allocation + PPS state after async creation window (~7s).
 * Pass `ppsCountBefore` from submit response to detect newly-created PPS.
 * If `isScopeToggleNeeded=true`, the order is stuck at Scope=1 — caller should
 * re-submit allocation with current committed values to retry the scope toggle.
 */
export const verifyAllocation = (orderId, ppsCountBefore, lineNumber) => {
  const params = {}
  if (ppsCountBefore !== undefined && ppsCountBefore !== null) {
    params.ppsCountBefore = ppsCountBefore
  }
  if (lineNumber !== undefined && lineNumber !== null) {
    params.lineNumber = lineNumber
  }
  return client.get(`/orders/${orderId}/allocation/verify`, { params })
}

export const getAuditTimeline = (orderId) => client.get(`/orders/${orderId}/audit-timeline`)

export const getHoldCodes = () => client.get('/hold-codes')
